import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PeopleRegular } from "@fluentui/react-icons";

import { Badge } from "@/components/ui/badge";
import {
  useVisitorAdditionalFields,
  useVisitorFields,
} from "@/lib/useVisitorAdditionalFields";
import {
  isSameField,
  prefillsNameFromOutlook,
} from "@/lib/visitorAdditionalFields";
import Spinner from "../components/Spinner";
import Visitor from "../components/Visitor";
import {
  GET_VISITORS_REQUEST,
  GET_VISITOR_TAGS_REQUEST,
  GET_VISITOR_MODULE_SETTINGS_REQUEST,
  GET_VISITOR_LANGUAGES_REQUEST,
  RESEND_INVITE_REQUEST,
} from "../redux/actions";

// Teilnehmer werden über ihre E-Mail identifiziert. Outlook liefert dieselbe
// Adresse je nach Quelle unterschiedlich geschrieben — ohne Normalisierung gilt
// derselbe Teilnehmer als neu, seine Karte wird ersetzt und alles Eingetippte
// ist weg.
const emailKey = (email) => (email || "").trim().toLowerCase();

/**
 * Zusatzfelder eines Teilnehmers: Aufbau aus der Feld-Konfiguration des
 * Standorts, Werte aus den vorhandenen Daten.
 *
 * Die am Besucher gespeicherten Felder tragen weder die Tag-Bindung
 * (`visible_for_tags`) noch die Pflichteinstellung — sie taugen als Wertquelle,
 * nicht als Struktur. Ist die Konfiguration noch nicht geladen, bleibt es beim
 * Gespeicherten, bis sie eintrifft.
 */
const buildFields = (config, ...valueSources) => {
  if (!config || config.length === 0) {
    return valueSources.find((v) => v && v.length) || [];
  }
  return config.map((field) => {
    for (const source of valueSources) {
      // Über die feste Feld-ID, wo beide eine haben, sonst über den Titel —
      // ältere Werte an Besuchern tragen oft keine ID.
      const hit = (source || []).find((f) => isSameField(f, field));
      if (hit) {
        const value = hit.inserted_value ?? hit.value ?? "";
        return { ...field, inserted_value: value, value };
      }
    }
    return { ...field };
  });
};

/** Eingaben des Benutzers über die frisch aufgebauten Daten legen. */
const applyDraft = (base, draft) => {
  if (!draft) return base;
  return {
    ...base,
    isChecked: draft.isChecked ?? base.isChecked,
    name: draft.name ?? base.name,
    company: draft.company ?? base.company,
    tagLabels: draft.tagLabels ?? base.tagLabels,
    doorPermissions: draft.doorPermissions ?? base.doorPermissions,
    language: draft.language ?? base.language,
    dynamicFields: draft.dynamicFields
      ? buildFields(base.dynamicFields, draft.dynamicFields)
      : base.dynamicFields,
  };
};

const Visitors = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const allVisitors = useSelector((state) => state.visitors.data) || [];
  const existingAppointmentVisitors = useSelector((state) => state.getAppointment.data.visitors) || [];
  const hosts = useSelector((state) => state.company.data.hosts) || [];
  // Zusatzfelder des Standorts (mit Rückfall auf die alten kundenweiten Felder,
  // solange für den Standort nichts gespeichert ist). Name, E-Mail und Firma
  // stehen nicht darin — ihre Werte gehören in die Spalten des Besuchers.
  const companyDynamicFields = useVisitorAdditionalFields(
    useSelector((state) => state.appointment.location_id),
  );
  // Ganze Feldliste mit Name, E-Mail und Firma: Reihenfolge, Bezeichnung und
  // Pflicht der Karten.
  const visitorFields = useVisitorFields(
    useSelector((state) => state.appointment.location_id),
  );
  // Visitor-Settings aller Standorte; `undefined`, solange sie nicht geladen
  // sind (bei einem Fehler `{}`).
  const visitorModuleSettings = useSelector(
    (state) => state.company.data.visitor_module_settings,
  );
  const customerName = useSelector((state) => state.company.data.customer_name);
  const customerId = useSelector((state) => state.company.data.customer_id);
  const ownEmail = useSelector((state) => state.user.data?.user?.email);
  // Verfügbare Visitor-Tags aus den Settings — kommen vom /visitor-tag/list-
  // Endpoint. Wenn der Customer keine Tags angelegt hat, blendet die Visitor-
  // Komponente die Sektion komplett aus.
  const visitorTags = useSelector((state) => state.company.data.visitor_tags) || [];
  const visitorLanguages = useSelector((state) => state.company.data.visitor_languages) || [];
  // Standort des Termins — Tags und Feld-Konfiguration gelten pro Standort.
  const locationId = useSelector((state) => state.appointment.location_id);
  const outlookId = useSelector((state) => state.appointment.outlook_id);
  // Zeiten des Outlook-Termins — für die Prüfung der maximalen Termindauer.
  const appointmentStart = useSelector((state) => state.appointment.date_time);
  const appointmentEnd = useSelector((state) => state.appointment.end_date_time);
  // Erneut einladen ergibt nur Sinn, wenn der Termin bereits gespeichert ist.
  const appointmentSaved = useSelector((state) => state.getAppointment.isSuccessful);
  const visitorRefs = useRef([]);

  const [localSelectedVisitors, setLocalSelectedVisitors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const selectedCount = localSelectedVisitors.filter((v) => v.isChecked).length;

  useEffect(() => {
    if (allVisitors.length === 0 && hosts.length > 0) {
      dispatch(GET_VISITORS_REQUEST());
    }
  }, [allVisitors, hosts]);

  useEffect(() => {
    if (customerId) {
      dispatch(GET_VISITOR_TAGS_REQUEST({ customer_id: customerId }));
      // Feld-Konfiguration aller Standorte (Bezeichnung, Pflicht, Einfach-/
      // Mehrfachauswahl) — der Standort ist im Termin wechselbar.
      dispatch(GET_VISITOR_MODULE_SETTINGS_REQUEST({ customer_id: customerId }));
      dispatch(GET_VISITOR_LANGUAGES_REQUEST({ customer_id: customerId }));
    }
  }, [customerId]);

  useEffect(() => {
    const updateAttendees = () => {
      dispatch(GET_VISITORS_REQUEST());
    };

    if (Office.context.mailbox.item) {
      const { addHandlerAsync, removeHandlerAsync } = Office.context.mailbox.item;
      const { RecipientsChanged } = Office.EventType;
      addHandlerAsync(RecipientsChanged, updateAttendees);

      return () => {
        removeHandlerAsync(RecipientsChanged, updateAttendees);
      };
    }
  }, []);

  // Teilnehmerliste aufbauen — läuft erneut, sobald Termindaten, Tags oder die
  // Feld-Konfiguration nachladen. Bestehende Einträge werden dabei nicht
  // eingefroren, sondern neu aufgebaut und die Eingaben des Benutzers darüber
  // gelegt: sonst erreicht eine später eintreffende Konfiguration die bereits
  // angelegten Karten nie.
  // Namen neuer Teilnehmer aus dem Outlook-Kontakt übernehmen? Einstellung am
  // Namen-Feld (Web: Einstellungen → Besucher → Felder → Name), ohne Wert an.
  const prefillName = prefillsNameFromOutlook(visitorFields);

  useEffect(() => {
    if (hosts.length === 0) return;
    // Erst aufbauen, wenn die Standort-Einstellungen da sind — sonst stünde
    // der Outlook-Name schon in der Karte, und die hält ihn als Eingabe fest,
    // auch wenn die Einstellung ihn gar nicht übernehmen will.
    if (customerId && visitorModuleSettings === undefined) return;

    // Gastgeber sind keine Besucher — und wer den Termin erstellt, steht als
    // Gastgeber oben, nicht in der Teilnehmerliste.
    const excludedHostEmails = new Set(hosts.map((host) => emailKey(host.email)));
    if (ownEmail) excludedHostEmails.add(emailKey(ownEmail));
    const existingAppointmentEmails = new Set(
      existingAppointmentVisitors.map((visitor) => emailKey(visitor.email)),
    );

    setLocalSelectedVisitors((previous) => {
      const drafts = new Map(previous.map((v) => [emailKey(v.email), v]));

      const next = allVisitors.reduce((acc, visitor) => {
        const key = emailKey(visitor.email);
        if (!key || excludedHostEmails.has(key)) return acc;

        const existingVisitor =
          existingAppointmentVisitors.find((ev) => emailKey(ev.email) === key) || {};

        const base = {
          ...visitor,
          ...existingVisitor,
          email: visitor.email,
          // Gespeicherte Besucher behalten ihren Namen; neue bekommen den aus
          // Outlook nur, wenn der Standort das so eingestellt hat.
          name:
            existingVisitor.name ?? (prefillName ? visitor.name : ""),
          isChecked: existingAppointmentEmails.has(key)
            ? true
            : existingVisitor.isChecked ?? visitor.isChecked,
          // Struktur aus der Konfiguration, Werte aus dem gespeicherten Besucher.
          dynamicFields: buildFields(
            companyDynamicFields,
            existingVisitor.additional_information,
          ),
          checked_in: existingVisitor.checked_in ?? false,
          badge_lost: existingVisitor.badge_lost ?? false,
          // Tags vom bestehenden Visitor übernehmen (Backend liefert sie
          // als Array von Tag-Objekten in `tags`). Labels reichen für die
          // Anzeige + Auswahl, beim Speichern wird's wieder als
          // tag_labels-Array Richtung Backend geschickt.
          tagLabels: (Array.isArray(existingVisitor.tags) ? existingVisitor.tags : [])
            .map((t) => t.tag_label)
            .filter(Boolean),
          // Nur bereits gespeicherte Besucher lassen sich erneut einladen.
          visitorId: existingVisitor.visitor_id ?? null,
          language: existingVisitor.language || null,
          doorPermissions: Array.isArray(existingVisitor.door_permissions)
            ? existingVisitor.door_permissions
            : [],
        };

        acc.push(applyDraft(base, drafts.get(key)));
        return acc;
      }, []);

      return next;
    });
    setIsLoading(false);
  }, [
    allVisitors,
    existingAppointmentVisitors,
    hosts,
    companyDynamicFields,
    ownEmail,
    prefillName,
    visitorModuleSettings,
    customerId,
  ]);

  // Erneuter Versand läuft über dieselbe Saga wie der normale Mailversand.
  const handleResendInvite = ({ visitorId, onDone }) => {
    if (!visitorId || !outlookId) {
      onDone?.(false);
      return;
    }
    dispatch(
      RESEND_INVITE_REQUEST({
        outlook_id: outlookId,
        visitor_id: visitorId,
        onDone,
      }),
    );
  };

  const handleCheckedChange = (visitorData) => {
    const key = emailKey(visitorData.email);
    setLocalSelectedVisitors((prev) =>
      prev.map((visitor) =>
        emailKey(visitor.email) === key ? { ...visitor, ...visitorData } : visitor,
      ),
    );
  };

  // Die Karte meldet ihren Stand laufend hierher. Damit überlebt eine Eingabe
  // auch dann, wenn die Liste neu aufgebaut wird — etwa weil Outlook die
  // Teilnehmer meldet oder eine Serverantwort eintrifft.
  const handleDraftChange = (draft) => {
    const key = emailKey(draft.email);
    if (!key) return;
    setLocalSelectedVisitors((prev) =>
      prev.map((visitor) =>
        emailKey(visitor.email) === key ? applyDraft(visitor, draft) : visitor,
      ),
    );
  };

  useEffect(() => {
    if (props.onCheckedVisitorsChange) {
      props.onCheckedVisitorsChange(localSelectedVisitors.filter((v) => v.isChecked).length);
    }
  }, [localSelectedVisitors]);

  const createAppointmentVisitors = () => {
    const validVisitors = visitorRefs.current
      .map((visitorRef) => {
        if (visitorRef && visitorRef.getVisitorData) {
          return visitorRef.getVisitorData();
        }
        return null;
      })
      .filter((visitor) => visitor && visitor.isChecked);

    dispatch({
      type: "SET_APPOINTMENT_DATA",
      payload: {
        visitors: validVisitors.map((visitor) => ({
          name: visitor.name,
          email: visitor.email,
          company: visitor.company,
          additional_information: visitor.dynamicFields,
          tag_labels: visitor.tagLabels || [],
          language: visitor.language || undefined,
          // Aus den Standardwerten der gewählten Tags abgeleitet, im Formular
          // anpassbar.
          door_permissions: visitor.doorPermissions || [],
        })),
      },
    });
  };

  useImperativeHandle(ref, () => ({
    triggerValidation: () => {
      let allValid = true;
      let allMissingFields = [];
      // Nur tatsächlich gerenderte Karten prüfen: im Ref-Array bleiben leere
      // Plätze zurück, wenn Teilnehmer wegfallen (Räume, Gastgeber, Postfach).
      // Die als „ungültig" zu werten, blockierte den Versand grundlos.
      visitorRefs.current
        .filter((visitorRef) => visitorRef && visitorRef.triggerValidation)
        .forEach((visitorRef) => {
          const result = visitorRef.triggerValidation();
          if (!result.isValid) {
            allValid = false;
            allMissingFields = allMissingFields.concat(result.missingFields);
          }
        });
      console.log("Overall validation result:", allValid);
      return { isValid: allValid, missingFields: allMissingFields };
    },
    createAppointmentVisitors,
    getVisitorsData: () => {
      return visitorRefs.current.map((visitorRef) => visitorRef && visitorRef.getVisitorData()).filter(Boolean);
    },
  }));

  return (
    <div>
      <div className="flex items-center justify-between gap-[1.2rem] mt-[2rem] mb-[0.4rem]">
        <span className="text-[1.3rem] font-semibold text-foreground">Teilnehmer</span>
        {localSelectedVisitors.length > 0 && (
          <Badge variant={selectedCount > 0 ? "primary" : "neutral"} size="sm">
            {selectedCount} von {localSelectedVisitors.length}
          </Badge>
        )}
      </div>
      {isLoading ? (
        <Spinner size="small" />
      ) : localSelectedVisitors.length > 0 ? (
        <div className="space-y-[0.6rem]">
          <p className="text-[1.2rem] text-dim m-0 mb-[0.8rem]">
            Wer eine Einladung erhalten soll, wird hier ausgewählt.
          </p>
          {localSelectedVisitors.map((visitor, index) => (
            <Visitor
              key={emailKey(visitor.email) || `idx-${index}`}
              ref={(el) => {
                visitorRefs.current[index] = el;
                visitorRefs.current.length = localSelectedVisitors.length;
              }}
              initialName={visitor.name}
              initialEmail={visitor.email}
              initialCompany={visitor.company}
              initiallyChecked={visitor.isChecked}
              dynamicFields={visitor.dynamicFields}
              fieldConfig={visitorFields}
              availableTags={visitorTags}
              availableLanguages={visitorLanguages}
              initialLanguage={visitor.language || null}
              locationId={locationId}
              appointmentStart={appointmentStart}
              appointmentEnd={appointmentEnd}
              initialTagLabels={visitor.tagLabels || []}
              initialDoorPermissions={visitor.doorPermissions || []}
              onCheckedChange={handleCheckedChange}
              onDraftChange={handleDraftChange}
              visitorId={visitor.visitorId}
              canResendInvite={appointmentSaved && !!visitor.visitorId}
              onResendInvite={handleResendInvite}
              isReadOnly={
                customerName === "ETH Zürich" && (visitor.checked_in === true || visitor.badge_lost === true)
              }
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-[2.4rem] border border-dashed border-gray rounded-card bg-bg">
          <PeopleRegular className="w-[3rem] h-[3rem] text-dim mx-auto mb-[0.6rem]" />
          <p className="text-[1.3rem] font-medium text-foreground m-0">
            Noch keine Teilnehmer
          </p>
          <p className="text-[1.2rem] text-dim m-0 mt-[0.3rem]">
            Fügen Sie dem Termin Teilnehmer hinzu.
          </p>
        </div>
      )}
    </div>
  );
});

export default Visitors;
